/**
 * Servicio para operaciones con imágenes en el backend
 */
import ApiService from './ApiService';

/**
 * Sube una imagen al backend
 * @param {File} archivo - Archivo de imagen a subir
 * @param {string} nombreArchivo - Nombre del archivo (opcional, se genera automáticamente si no se proporciona)
 * @returns {Promise<string>} URL o nombre del archivo devuelto por el backend
 */
export const subirImagen = async (archivo, nombreArchivo = null) => {
  try {
    // Validar que sea un archivo de imagen
    if (!archivo.type.startsWith('image/')) {
      throw new Error('El archivo debe ser una imagen');
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (archivo.size > maxSize) {
      throw new Error('La imagen no puede superar los 5MB');
    }

    // Crear FormData para enviar el archivo
    const formData = new FormData();
    formData.append('imagenURL', archivo);
    if (nombreArchivo) {
      formData.append('nombreArchivo', nombreArchivo);
    }

    // Subir archivo al backend
    // El endpoint espera: POST /productos/imagen o POST /upload o POST /upload/imagen
    // Ajusta la ruta según tu backend
    let endpoint = '/productos/imagen';
    
    // Intentar diferentes endpoints posibles si el primero falla
    const endpointsPosibles = [
      '/imagenURL',
      '/productos/imagen',
      '/upload',
      '/upload/imagen',
      '/api/productos/imagen',
      '/imagenes/upload'
    ];
    
    let lastError = null;
    let response = null;
    const totalEndpoints = endpointsPosibles.length;
    
    for (let i = 0; i < endpointsPosibles.length; i++) {
      const ep = endpointsPosibles[i];
      try {
        response = await ApiService.post(ep, formData, {
          headers: {}, // No agregar headers, FormData los maneja automáticamente
        });
        endpoint = ep;
        break; // Si funciona, salir del bucle
      } catch (err) {
        lastError = err;
        // Si es 404 y no es el último endpoint, intentar el siguiente
        if (err.status === 404 && i < totalEndpoints - 1) {
          console.warn(`Endpoint ${ep} no encontrado (404), intentando siguiente...`);
          continue;
        }
        // Si es otro error (no 404) o es el último endpoint, lanzar error
        throw err;
      }
    }
    
    if (!response && lastError) {
      throw lastError;
    }

    // El backend debería devolver la URL o nombre del archivo
    // Ajusta según la estructura de respuesta de tu backend
    // Ejemplo de respuestas posibles:
    // { url: 'https://...', filename: 'imagen.jpg', path: '/uploads/imagen.jpg' }
    const imagenURL = response?.url || response?.filename || response?.path || response?.imagenURL || response;
    
    if (!imagenURL) {
      throw new Error('El servidor no devolvió la URL de la imagen');
    }

    console.log(`Imagen subida exitosamente usando endpoint: ${endpoint}`);
    return imagenURL;
  } catch (error) {
    console.error('Error al subir imagen:', error);
    
    // Si es un error 404, proporcionar mensaje más útil
    if (error.status === 404) {
      throw new Error('El endpoint para subir imágenes no está disponible en el backend. Por favor, verifica la configuración del servidor o contacta al administrador.');
    }
    
    throw new Error(error.message || 'Error al subir la imagen');
  }
};

/**
 * Elimina una imagen del backend
 * @param {string} urlImagen - URL o nombre de la imagen a eliminar
 * @returns {Promise<void>}
 */
export const eliminarImagen = async (urlImagen) => {
  try {
    if (!urlImagen) {
      throw new Error('URL de imagen no válida');
    }

    // Extraer el nombre del archivo o la ruta
    let nombreArchivo = urlImagen;
    
    // Si es una URL completa, extraer solo el nombre del archivo
    if (urlImagen.includes('http')) {
      try {
        const urlObj = new URL(urlImagen);
        nombreArchivo = urlObj.pathname.split('/').pop();
      } catch (e) {
        // Si falla el parseo, usar la URL completa
        nombreArchivo = urlImagen;
      }
    }

    // Si es una ruta local (ej: 'nombre-imagen.png'), usar directamente
    if (!urlImagen.includes('http')) {
      nombreArchivo = urlImagen.split('/').pop();
    }

    // Llamar al endpoint de eliminación del backend
    // El endpoint espera: DELETE /productos/imagen/:nombreArchivo
    // Ajusta la ruta según tu backend
    try {
      await ApiService.del(`/productos/imagen/${encodeURIComponent(nombreArchivo)}`);
    } catch (error) {
      // Si el error es 404, la imagen ya no existe, no lanzar error
      if (error.status === 404) {
        console.warn('La imagen no existe en el servidor:', nombreArchivo);
        return;
      }
      throw error;
    }
  } catch (error) {
    console.error('Error al eliminar imagen:', error);
    throw new Error(error.message || 'Error al eliminar la imagen');
  }
};

/**
 * Extrae el nombre del archivo desde una URL del backend o una ruta local
 * @param {string} url - URL o ruta de la imagen
 * @returns {string} Nombre del archivo
 */
export const obtenerNombreArchivoDesdeURL = (url) => {
  if (!url) return null;
  
  // Si es una URL completa (del backend o externa)
  if (url.includes('http')) {
    try {
      const urlObj = new URL(url);
      // Extraer el nombre del archivo de la ruta
      const pathname = urlObj.pathname;
      const nombreArchivo = pathname.split('/').pop();
      if (nombreArchivo && nombreArchivo !== '') {
        return decodeURIComponent(nombreArchivo);
      }
    } catch (e) {
      console.error('Error al parsear URL:', e);
    }
  }
  
  // Si es una ruta local (como 'nombre-imagen.png' o '/images/nombre-imagen.png')
  if (!url.includes('http')) {
    return url.split('/').pop();
  }
  
  return null;
};

